import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiAlertCircle, FiDollarSign, FiTrendingUp } from 'react-icons/fi';
import { pageTransition, staggerItem, tapScale, tapScaleTransition } from '../helpers/animations';
import { api, normalizeToken, tokenPath } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import SearchableSelect from '../components/SearchableSelect';
import { SellFormSkeleton } from '../components/skeletons';

const MIN_INR = 500;

function normalizeBanks(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const type = String(item?.type || '').toLowerCase() === 'upi' ? 'upi' : 'bank';
      const accountNo = String(item?.account_no ?? '').trim();
      const holder = String(item?.account_holder ?? item?.name ?? '').trim();
      const upiId = String(item?.upi_id ?? (type === 'upi' ? accountNo : '')).trim();
      const bankName = String(item?.bank_name || '').trim();
      const bankId = String(item?.bank_id ?? item?.id ?? `${type}-${index}`);

      return {
        bank_id: bankId,
        type,
        bank_name: bankName,
        account_no: accountNo,
        account_holder: holder,
        ifsc_code: String(item?.ifsc_code || '').trim().toUpperCase(),
        upi_id: upiId,
        name: String(item?.name ?? holder).trim(),
      };
    })
    .filter((item) => (item.type === 'upi' ? item.upi_id || item.account_no : item.account_no || item.bank_name));
}

function bankOptionLabel(item) {
  if (item.type === 'upi') {
    return `UPI · ${item.upi_id || item.account_no}${item.name ? ` (${item.name})` : ''}`;
  }
  const masked =
    item.account_no.length > 4 ? `••••${item.account_no.slice(-4)}` : item.account_no || '—';
  return `${item.bank_name || 'Bank'} · ${masked}${item.account_holder ? ` · ${item.account_holder}` : ''}`;
}

function apiErrorMessage(data, fallback) {
  if (typeof data?.error === 'string' && data.error) return data.error;
  return fallback;
}

function SellMerchants() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const navigate = useNavigate();

  const [usdtBalance, setUsdtBalance] = useState(null);
  const [rate, setRate] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [bankId, setBankId] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSessionExpired = useCallback(() => {
    toast.error('Session expired. Please open your access link again.');
    navigate('/', { replace: true });
  }, [navigate]);

  const refreshProfileBalance = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.post(`${API_BASE_URL}/exchange/profile`, { token });
      if (data?.error === false) {
        setUsdtBalance(Number(data?.data?.usdt ?? 0));
      } else if (data?.error === 'session expired') {
        handleSessionExpired();
      }
    } catch {
      // api helper toast
    }
  }, [handleSessionExpired, token]);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        handleSessionExpired();
        return;
      }

      setLoading(true);
      try {
        const [profileRes, rateRes, bankRes] = await Promise.all([
          api.post(`${API_BASE_URL}/exchange/profile`, { token }),
          api.post(`${API_BASE_URL}/exchange/exchange-sell-rate`, { token }),
          api.post(`${API_BASE_URL}/exchange/bank-list`, { token }),
        ]);

        const profileData = profileRes?.data;
        if (profileData?.error === 'session expired') {
          handleSessionExpired();
          return;
        }
        if (profileData?.error === false) {
          setUsdtBalance(Number(profileData?.data?.usdt ?? 0));
        } else if (profileData?.error) {
          toast.error(apiErrorMessage(profileData, 'Failed to fetch profile'));
        }

        const rateData = rateRes?.data;
        if (rateData?.error === 'session expired') {
          handleSessionExpired();
          return;
        }
        if (rateData?.error === false && rateData?.data?.rate != null) {
          setRate(Number(rateData.data.rate));
        } else if (rateData?.error) {
          toast.error(apiErrorMessage(rateData, 'Failed to fetch sell rate'));
        }

        const bankData = bankRes?.data;
        if (bankData?.error === 'session expired') {
          handleSessionExpired();
          return;
        }
        if (bankData?.error === false) {
          setBanks(normalizeBanks(bankData?.data));
        } else if (bankData?.error) {
          toast.error(apiErrorMessage(bankData, 'Failed to fetch bank list'));
        }
      } catch {
        // api helper toast
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [handleSessionExpired, token]);

  const expectedInr = useMemo(() => {
    const usdt = Number(amount);
    if (!rate || !Number.isFinite(usdt) || usdt <= 0) return null;
    return usdt * rate;
  }, [amount, rate]);

  const minUsdt = useMemo(() => {
    if (!rate || rate <= 0) return null;
    return MIN_INR / rate;
  }, [rate]);

  const selectedBank = useMemo(
    () => banks.find((item) => item.bank_id === bankId) || null,
    [banks, bankId]
  );

  const bankOptions = useMemo(
    () => banks.map((item) => ({ value: item.bank_id, label: bankOptionLabel(item) })),
    [banks]
  );

  const validateAmount = useCallback(
    (value) => {
      if (!value) return 'Please enter USDT amount';
      const usdt = Number(value);
      if (!Number.isFinite(usdt) || usdt <= 0) return 'Please enter a valid amount';
      if (usdtBalance != null && usdt > usdtBalance) {
        return `Amount exceeds available balance (${usdtBalance.toFixed(2)} USDT)`;
      }
      if (rate == null) return 'Sell rate unavailable';
      if (Number.isFinite(usdt * rate) && usdt * rate < MIN_INR) {
        const minLabel = minUsdt != null ? minUsdt.toFixed(4) : '—';
        return `Minimum withdrawal value is ₹${MIN_INR} (about ${minLabel} USDT)`;
      }
      return '';
    },
    [minUsdt, rate, usdtBalance]
  );

  const amountError = validateAmount(amount);
  const bankError = !bankId ? 'Please select a bank / UPI account' : '';
  const canSubmit = !loading && !submitting && !amountError && !bankError && rate != null;

  const handleAmountChange = (event) => {
    const raw = event.target.value.replace(/[^\d.]/g, '');
    const parts = raw.split('.');
    const next = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : raw;
    const limited = next.includes('.')
      ? `${next.split('.')[0]}.${(next.split('.')[1] || '').slice(0, 6)}`
      : next;

    setAmount(limited);
    setErrors((prev) => ({ ...prev, amount: validateAmount(limited) }));
  };

  const handleUseMax = () => {
    if (usdtBalance == null || usdtBalance <= 0) return;
    const max = Number(usdtBalance).toFixed(6).replace(/\.?0+$/, '');
    setAmount(max);
    setErrors((prev) => ({ ...prev, amount: validateAmount(max) }));
  };

  const handleBankChange = (nextId) => {
    setBankId(nextId);
    setErrors((prev) => ({
      ...prev,
      bankId: nextId ? '' : 'Please select a bank / UPI account',
    }));
  };

  const resetForm = () => {
    setAmount('');
    setBankId('');
    setErrors({});
    setShowConfirm(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextAmountError = validateAmount(amount);
    const nextBankError = !bankId ? 'Please select a bank / UPI account' : '';
    setErrors({ amount: nextAmountError, bankId: nextBankError });
    if (nextAmountError || nextBankError || rate == null) return;
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    if (submitting) return;

    const nextAmountError = validateAmount(amount);
    const nextBankError = !bankId ? 'Please select a bank / UPI account' : '';
    setErrors({ amount: nextAmountError, bankId: nextBankError });
    if (nextAmountError || nextBankError || rate == null) {
      setShowConfirm(false);
      return;
    }

    if (!token) {
      handleSessionExpired();
      return;
    }

    const usdt = Number(amount);
    setSubmitting(true);
    try {
      const { data } = await api.post(`${API_BASE_URL}/exchange/withdrawal-request`, {
        token,
        usdt,
        bank_id: bankId,
      });

      if (data?.error === false) {
        toast.success(data?.msg || 'Withdrawal request submitted successfully');
        resetForm();
        await refreshProfileBalance();
        navigate(tokenPath(token, 'sell-report'));
      } else if (data?.error === 'session expired') {
        handleSessionExpired();
      } else if (data?.error) {
        toast.error(apiErrorMessage(data, 'Failed to submit withdrawal request'));
        setShowConfirm(false);
      }
    } catch {
      // api helper toast (network / 500)
      setShowConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <motion.h1 className="screen-title" variants={staggerItem} initial="initial" animate="animate">
        Sell USDT
      </motion.h1>
      <motion.p className="buy-merchant-back" variants={staggerItem} initial="initial" animate="animate">
        <Link to={tokenPath(token, 'trade')}>Back to Trade</Link>
      </motion.p>

      <motion.div
        className="card sell-form-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.25 }}
      >
        {loading ? (
          <SellFormSkeleton />
        ) : (
          <>
        <h2 className="card-title">Sell details</h2>
        <p className="card-subtitle">Enter USDT amount and choose where to receive INR.</p>

        <div className="sell-balance-chip">
          <span className="sell-balance-icon" aria-hidden>
            <FiDollarSign size={16} />
          </span>
          <div className="sell-balance-text">
            <span className="sell-balance-label">Available USDT</span>
            <span className="sell-balance-value">
              {loading || usdtBalance == null ? '—' : Number(usdtBalance).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="rate-summary" style={{ marginTop: 12 }}>
          {loading && (
            <div className="rate-summary-row">
              <FiTrendingUp size={14} className="rate-summary-icon" />
              <span className="rate-summary-label">Fetching sell rate...</span>
            </div>
          )}
          {!loading && rate != null && (
            <div className="rate-summary-row">
              <FiTrendingUp size={14} className="rate-summary-icon" />
              <span className="rate-summary-label">
                <span className="rate-summary-muted">Sell rate</span>
                <strong className="rate-summary-strong">1 USDT = ₹{rate}</strong>
              </span>
            </div>
          )}
          {!loading && rate == null && (
            <div className="rate-summary-row">
              <FiTrendingUp size={14} className="rate-summary-icon" />
              <span className="rate-summary-label">Sell rate unavailable</span>
            </div>
          )}
        </div>

        <form className="form" style={{ marginTop: 14 }} onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="sell-usdt-amount">USDT amount</label>
            <div className={`input-with-icon sell-amount-field${errors.amount ? ' has-error' : ''}`}>
              <input
                id="sell-usdt-amount"
                type="text"
                inputMode="decimal"
                placeholder="Enter USDT to sell"
                value={amount}
                onChange={handleAmountChange}
                className={errors.amount ? 'input-error' : ''}
                disabled={loading || submitting}
              />
              {usdtBalance != null && usdtBalance > 0 && (
                <button
                  type="button"
                  className="sell-amount-max"
                  onClick={handleUseMax}
                  disabled={loading || submitting}
                >
                  Max
                </button>
              )}
            </div>
            {errors.amount && <p className="field-error">{errors.amount}</p>}
            {expectedInr != null && !amountError && (
              <p className="field-hint">
                You will receive approximately <strong>₹{expectedInr.toFixed(2)}</strong>
              </p>
            )}
            {minUsdt != null && (
              <p className="form-hint" style={{ marginTop: 4 }}>
                Minimum withdrawal: ₹{MIN_INR} (~{minUsdt.toFixed(4)} USDT)
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="sell-bank">Receive to</label>
            <SearchableSelect
              id="sell-bank"
              options={bankOptions}
              value={bankId}
              onChange={handleBankChange}
              placeholder={
                loading ? 'Loading accounts…' : banks.length === 0 ? 'No bank accounts found' : 'Select bank / UPI'
              }
              searchPlaceholder="Search your accounts…"
              disabled={loading || submitting || banks.length === 0}
              error={!!errors.bankId}
              placement="top"
            />
            {errors.bankId && <p className="field-error">{errors.bankId}</p>}
            {!loading && banks.length === 0 && (
              <p className="form-hint" style={{ marginTop: 6 }}>
                Add a bank or UPI from{' '}
                <Link to={tokenPath(token, 'bank-accounts')} className="sell-inline-link">
                  Bank accounts
                </Link>
                .
              </p>
            )}
          </div>

          <motion.button
            type="submit"
            className="btn primary"
            disabled={!canSubmit}
            whileTap={canSubmit ? tapScale : undefined}
            transition={tapScaleTransition}
          >
            {submitting ? 'Submitting…' : 'Submit sell request'}
          </motion.button>
        </form>
          </>
        )}
      </motion.div>

      <AnimatePresence>
        {showConfirm && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="modal-header">
                <h2 className="card-title">Confirm withdrawal</h2>
                <p className="card-subtitle">Review the sell details before submitting.</p>
              </div>

              <div className="modal-body">
                <div className="modal-summary-row">
                  <div className="modal-summary-chip">
                    <span className="modal-summary-label">You sell</span>
                    <span className="modal-summary-value">{Number(amount || 0).toFixed(2)} USDT</span>
                  </div>
                  <div className="modal-summary-chip">
                    <span className="modal-summary-label">You receive</span>
                    <span className="modal-summary-value">
                      ₹{(expectedInr != null ? expectedInr : 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="modal-info-card">
                  <div className="modal-info-row">
                    <span className="modal-info-label">Sell rate</span>
                    <span className="modal-info-value">1 USDT = ₹{rate}</span>
                  </div>
                  <div className="modal-info-row">
                    <span className="modal-info-label">Receive to</span>
                    <span className="modal-info-value">
                      {selectedBank ? bankOptionLabel(selectedBank) : '—'}
                    </span>
                  </div>
                </div>

                <div className="modal-warning-card">
                  <div className="modal-warning-title">
                    <FiAlertCircle size={14} />
                    <span>Important</span>
                  </div>
                  <ul className="confirm-terms">
                    <li>USDT will be deducted from your available balance.</li>
                    <li>INR will be sent to the selected bank / UPI account.</li>
                    <li>Withdrawals cannot be cancelled once submitted.</li>
                  </ul>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn ghost btn-sm"
                  onClick={() => setShowConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn primary btn-sm"
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting…' : 'Confirm & submit'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default SellMerchants;
